import { useEffect, useState } from 'react';
import { FileText, Printer, Search } from 'lucide-react';
import { toast } from 'sonner';
import { comprobantesCajaApi } from '../lib/api';
import { fechaLima } from '../lib/fechas';
import ComprobanteTicket, { ComprobanteCaja, NOMBRE_CAJA } from '../components/ComprobanteTicket';

// 2026-10-22 (fase 2): lista de comprobantes de caja RI (ingresos) y RE (egresos). Cada
// movimiento de dinero recibe su número al registrarse; un movimiento anulado deja su
// comprobante marcado ANULADO (la numeración nunca tiene huecos).
const ComprobantesCaja = () => {
  const hoy = fechaLima();
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [caja, setCaja] = useState('');
  const [tipo, setTipo] = useState('');
  const [q, setQ] = useState('');
  const [lista, setLista] = useState<ComprobanteCaja[]>([]);
  const [cargando, setCargando] = useState(false);
  const [imprimir, setImprimir] = useState<ComprobanteCaja | null>(null);

  const cargar = async () => {
    setCargando(true);
    try { setLista(await comprobantesCajaApi.listar({ desde, hasta, caja, tipo, q })); }
    catch (e: any) { toast.error('Error: ' + e.message); }
    finally { setCargando(false); }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (!imprimir) return;
    const t = setTimeout(() => { window.print(); setImprimir(null); }, 300);
    return () => clearTimeout(t);
  }, [imprimir]);

  const vigentes = lista.filter(c => !c.anulado);
  const totalRI = vigentes.filter(c => c.tipo === 'RI').reduce((s, c) => s + Number(c.monto), 0);
  const totalRE = vigentes.filter(c => c.tipo === 'RE').reduce((s, c) => s + Number(c.monto), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><FileText className="text-blue-600" size={30} /> Comprobantes de caja</h1>
          <p className="text-gray-600 text-sm mt-1">
            <b>RI</b> = Recibo de Ingreso (entra dinero) · <b>RE</b> = Recibo de Egreso (sale dinero). Se numeran solos al registrar cada movimiento.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 flex flex-wrap gap-3 items-end">
          <div><label className="block text-xs font-medium mb-1">Desde</label><input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="border rounded-lg px-3 py-2" /></div>
          <div><label className="block text-xs font-medium mb-1">Hasta</label><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="border rounded-lg px-3 py-2" /></div>
          <div><label className="block text-xs font-medium mb-1">Caja</label>
            <select value={caja} onChange={e => setCaja(e.target.value)} className="border rounded-lg px-3 py-2">
              <option value="">Todas</option>{Object.entries(NOMBRE_CAJA).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className="block text-xs font-medium mb-1">Tipo</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)} className="border rounded-lg px-3 py-2">
              <option value="">RI y RE</option><option value="RI">RI · Ingresos</option><option value="RE">RE · Egresos</option>
            </select></div>
          <div className="flex-1 min-w-[180px]"><label className="block text-xs font-medium mb-1">Buscar (número o concepto)</label>
            <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') cargar(); }} placeholder="RI-000012, bus 1003…" className="w-full border rounded-lg px-3 py-2" /></div>
          <button onClick={cargar} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"><Search size={16} /> Buscar</button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4"><p className="text-sm text-green-800">Ingresos (RI)</p><p className="text-2xl font-bold text-green-700">S/ {totalRI.toFixed(2)}</p></div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4"><p className="text-sm text-red-800">Egresos (RE)</p><p className="text-2xl font-bold text-red-700">S/ {totalRE.toFixed(2)}</p></div>
          <div className="bg-gray-50 border rounded-xl p-4"><p className="text-sm text-gray-700">Anulados</p><p className="text-2xl font-bold text-gray-700">{lista.length - vigentes.length}</p></div>
        </div>
        <div className="bg-white rounded-xl shadow overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr>
              <th className="px-3 py-2 text-left">Número</th><th className="px-3 py-2 text-left">Fecha</th><th className="px-3 py-2 text-left">Caja</th>
              <th className="px-3 py-2 text-left">Concepto</th><th className="px-3 py-2 text-left">Forma</th><th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2 text-left">Registró</th><th className="px-3 py-2 text-center">Ticket</th>
            </tr></thead>
            <tbody>
              {cargando ? <tr><td colSpan={8} className="p-6 text-center text-gray-500">Cargando…</td></tr>
                : lista.length === 0 ? <tr><td colSpan={8} className="p-6 text-center text-gray-500">No hay comprobantes en ese rango.</td></tr>
                : lista.map(c => (
                <tr key={c.numero} className={`border-t ${c.anulado ? 'bg-gray-50 text-gray-400 line-through' : ''}`}>
                  <td className="px-3 py-2 font-mono font-bold">
                    <span className={c.tipo === 'RI' ? 'text-green-700' : 'text-red-700'}>{c.numero}</span>
                    {c.anulado && <span className="ml-2 no-underline text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded" style={{ textDecoration: 'none' }}>ANULADO</span>}
                  </td>
                  <td className="px-3 py-2">{new Date(c.fecha).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  <td className="px-3 py-2">{NOMBRE_CAJA[c.caja || ''] || c.caja}</td>
                  <td className="px-3 py-2">{c.concepto}</td>
                  <td className="px-3 py-2">{(c.metodo_pago || 'efectivo') === 'efectivo' ? 'Efectivo' : 'Yape'}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${c.tipo === 'RI' ? 'text-green-700' : 'text-red-700'}`}>{c.tipo === 'RE' ? '-' : ''}S/ {Number(c.monto).toFixed(2)}</td>
                  <td className="px-3 py-2 capitalize">{c.usuario}</td>
                  <td className="px-3 py-2 text-center">
                    <button onClick={() => setImprimir(c)} className="text-blue-600 hover:text-blue-800" title="Imprimir comprobante"><Printer size={18} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {imprimir && <ComprobanteTicket c={imprimir} />}
    </div>
  );
};

export default ComprobantesCaja;
